import React from 'react';
import { Link } from "react-router-dom";
import { Box, Container, Grid, Typography, IconButton, Stack, Divider, alpha } from '@mui/material';
import { useLanguage } from "../../i18n/LanguageContext";
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Theme Colors for Coherence
const deepBlue = '#041e42'; // More professional deeper navy
const brandOrange = '#DD8748';

const Footer = () => {
    const { t, lang } = useLanguage();

    const navLinks = [
        { name: t('common.home'), path: '/' },
        { name: t('common.about'), path: '/About' },
        { name: t('common.services'), path: '/blog' },
        { name: t('common.packs'), path: '/Pack' },
        { name: t('common.contact'), path: '/Contact' }
    ];
  
    const products = [
        { name: t('common.CRM'), path: 'https://24-crm.tn/' },
        { name: t('common.Paie'), path: '#' },
        { name: t('common.Gold'), path: '#' },
        { name: 'E-COMMERCE', path: 'http://www.24-crm.online/' },
        { name: 'PolyPointage', path: '#' },
        { name: 'GenRS', path: '#' },
    ];

    return (
        <Box
            component="footer"
            sx={{
                backgroundColor: deepBlue,
                color: '#ffffff',
                pt: { xs: 8, md: 15 },
                pb: 6,
                mt: 'auto',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Subtle Gradient Overlay for depth */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: `radial-gradient(circle at 0% 0%, ${alpha(brandOrange, 0.03)} 0%, transparent 50%)`,
                    pointerEvents: 'none'
                }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Grid container spacing={8}>
                    {/* Column 1: Brand Personality */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ mb: 4 }}>
                            <img
                                src="./images/logobb.png"
                                alt="Polysoft Logo"
                                style={{ width: "210px", height: "auto", marginBottom: "35px" }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: alpha('#fff', 0.65),
                                    lineHeight: 1.9,
                                    maxWidth: 340,
                                    mb: 4,
                                    fontSize: '0.95rem',
                                    fontWeight: 400
                                }}
                            >
                                {/* {t('expertise.description').substring(0, 180)}... */}
                            </Typography>

                            <Stack direction="row" spacing={2.5}>
                                {[
                                    { icon: <FacebookIcon fontSize="small" />, link: 'https://www.facebook.com/polysoftco/?locale=fr_FR' },
                                    { icon: <LinkedInIcon fontSize="small" />, link: 'https://www.linkedin.com/company/34630481/admin/dashboard/' }
                                ].map((social, i) => (
                                    <IconButton
                                        key={i}
                                        href={social.link}
                                        target="_blank"
                                        sx={{
                                            color: '#fff',
                                            border: `1px solid ${alpha('#fff', 0.15)}`,
                                            p: 1.2,
                                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                bgcolor: brandOrange,
                                                borderColor: brandOrange,
                                                transform: 'translateY(-5px)',
                                                boxShadow: `0 10px 20px ${alpha(brandOrange, 0.3)}`
                                            }
                                        }}
                                    >
                                        {social.icon}
                                    </IconButton>
                                ))}
                            </Stack>
                        </Box>
                    </Grid>

                    {/* Column 2: Navigation Map */}
                    <Grid item xs={6} md={2}>
                        <Typography
                            variant="h6"
                            fontWeight={800}
                            sx={{
                                mb: 4,
                                color: brandOrange,
                                letterSpacing: 2,
                                fontSize: '0.8rem',
                                textTransform: 'uppercase'
                            }}
                        >
                            {lang === 'ar' ? 'التنقل' : 'Navigation'}
                        </Typography>
                        <Stack spacing={2.5}>
                            {navLinks.map((link) => (
                                <Box key={link.name} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Link
                                        to={link.path}
                                        style={{
                                            color: alpha('#fff', 0.8),
                                            textDecoration: 'none',
                                            fontSize: '0.95rem',
                                            fontWeight: 500,
                                            transition: '0.3s ease',
                                        }}
                                        onMouseOver={(e) => {
                                            e.target.style.color = brandOrange;
                                            e.target.style.transform = 'translateX(8px)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.color = alpha('#fff', 0.8);
                                            e.target.style.transform = 'translateX(0)';
                                        }}
                                    >
                                        {link.name}
                                    </Link>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>

                    {/* Column 3: Top Products */}
                    <Grid item xs={6} md={2}>
                        <Typography
                            variant="h6"
                            fontWeight={800}
                            sx={{
                                mb: 4,
                                color: brandOrange,
                                letterSpacing: 2,
                                fontSize: '0.8rem',
                                textTransform: 'uppercase'
                            }}
                        >
                            {lang === 'ar' ? 'منتجاتنا' : 'Solutions'}
                        </Typography>
                        <Stack spacing={2.5}>
                        {products.map((link) => (
    link.path.startsWith('http') ? (
        <a
            key={link.name}
            href={link.path}
            target="_blank"
            rel="noopener noreferrer"
            style={{
                color: alpha('#fff', 0.8),
                textDecoration: 'none',
                fontSize: '0.95rem',
                fontWeight: 500,
                transition: '0.3s ease',
            }}
            onMouseOver={(e) => {
                e.target.style.color = brandOrange;
                e.target.style.transform = 'translateX(8px)';
            }}
            onMouseOut={(e) => {
                e.target.style.color = alpha('#fff', 0.8);
                e.target.style.transform = 'translateX(0)';
            }}
        >
            {link.name}
        </a>
    ) : (
        <Link
            key={link.name}
            to={link.path}
            style={{
                color: alpha('#fff', 0.8),
                textDecoration: 'none',
                fontSize: '0.95rem',
                fontWeight: 500,
                transition: '0.3s ease',
            }}
        >
            {link.name}
        </Link>
    )
))}
                            {/* {products.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    style={{
                                        color: alpha('#fff', 0.8),
                                        textDecoration: 'none',
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        transition: '0.3s ease',
                                    }}
                                    onMouseOver={(e) => {
                                        e.target.style.color = brandOrange;
                                        e.target.style.transform = 'translateX(8px)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.target.style.color = alpha('#fff', 0.8);
                                        e.target.style.transform = 'translateX(0)';
                                    }}
                                >
                                    {link.name}
                                </Link>
                            ))} */}
                        </Stack>
                    </Grid>

                    {/* Column 4: Expertise Connect */}
                    <Grid item xs={12} md={4}>
                        <Typography
                            variant="h6"
                            fontWeight={800}
                            sx={{
                                mb: 4,
                                color: brandOrange,
                                letterSpacing: 2,
                                fontSize: '0.8rem',
                                textTransform: 'uppercase'
                            }}
                        >
                            {lang === 'ar' ? 'تواصل معنا' : 'Contact us'}
                        </Typography>
                  
                        <Stack spacing={4}>
                            <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
                                <Box sx={{ p: 1.2, bgcolor: alpha('#fff', 0.05), borderRadius: 2, color: brandOrange }}>
                                    <PhoneIcon fontSize="small" />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ display: 'block', color: alpha('#fff', 0.4), fontWeight: 800, letterSpacing: 1 }}> {t('contact.phone')}</Typography>
                                    <Typography variant="body1" fontWeight={700} sx={{ mt: -0.5, color: '#fff' ,direction: 'ltr'}} >+ 216 74 433 020</Typography> {/* {t('contact.tel')}*/}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
                                <Box sx={{ p: 1.2, bgcolor: alpha('#fff', 0.05), borderRadius: 2, color: brandOrange }}>
                                    <EmailIcon fontSize="small" />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ display: 'block', color: alpha('#fff', 0.4), fontWeight: 800, letterSpacing: 1 }}> {t('contact.support')}</Typography>
                                    <a href="https://mail.google.com/mail/u/0/#inbox"> <Typography variant="body1" fontWeight={700} sx={{ mt: -0.5, color: '#fff' }}>gm@polysoftco.tn</Typography></a>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                                <Box sx={{ p: 1.2, bgcolor: alpha('#fff', 0.05), borderRadius: 2, color: brandOrange, mt: 0.5 }}>
                                    <LocationOnIcon fontSize="small" />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ display: 'block', color: alpha('#fff', 0.4), fontWeight: 800, letterSpacing: 1 }}>{t('contact.address')} </Typography>
                                    <a href="https://www.google.com/maps/place/POLYSOFT/@34.753891,10.758659,19z/data=!4m6!3m5!1s0x1301d3fcbdf5f629:0x2fe90eeb77a94e4b!8m2!3d34.753796!4d10.7584861!16s%2Fg%2F11hnpsh09s?entry=ttu">
             <Typography variant="body1" fontWeight={700} sx={{ mt: -0.5, maxWidth: 260, color: '#fff' }}>{t('contact.address_val')}</Typography></a>
                                </Box>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 10, borderColor: alpha('#fff', 0.08) }} />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 3 }}>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.45), textAlign: { xs: 'center', md: 'left' }, fontSize: '0.85rem' }}>
                        © 2026 POLYSOFT & CO. {lang === 'ar' ? 'جميع الحقوق محفوظة.' : 'Tous droits réservés.'}
                    </Typography>

                    <Stack direction="row" spacing={5} sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.3), cursor: 'pointer', transition: '0.3s', '&:hover': { color: brandOrange } }}>Privacy Policy</Typography>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.3), cursor: 'pointer', transition: '0.3s', '&:hover': { color: brandOrange } }}>Terms of Service</Typography>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.3), cursor: 'pointer', transition: '0.3s', '&:hover': { color: brandOrange } }}>Cookie Settings</Typography>
                    </Stack>
                </Box>
            </Container>

            {/* Premium WhatsApp Button */}
            <Box
                component="a"
                href="https://wa.me/+21620413577"
                target="_blank"
                rel="noreferrer"
                sx={{
                    position: 'fixed',
                    bottom: 40,
                    right: lang === 'ar' ? 'auto' : 40,
                    left: lang === 'ar' ? 40 : 'auto',
                    width: 66,
                    height: 66,
                    bgcolor: '#25D366',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 12px 35px rgba(37, 211, 102, 0.35)',
                    zIndex: 9999,
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                        transform: 'scale(1.1) rotate(10deg)',
                        boxShadow: '0 18px 45px rgba(37, 211, 102, 0.5)',
                        bgcolor: '#20bd5a'
                    }
                }}
            >
                <img src="./images/whatsapp.png" alt="WhatsApp" style={{ width: '34px', height: '34px' }} />
            </Box>
        </Box>
    );
};

export default Footer;